import {css, cx} from '@emotion/css';
import React, {HTMLAttributes} from 'react';
import {Button, Modal, PayPalButton, Span} from './';
import {formatValueToDollar, GIFT_CARD_FACTOR, theme} from './Common';
import {GiftCardIcon} from '../Assets';
import {darken, lighten} from 'polished';

export type CustomerPortalModalCashOutProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => unknown;
  value: number;
  onClickCheck: () => unknown;
  onClickPaypal: () => unknown;
  onClickGiftCard?: null | (() => unknown);
};

const extraPercentage = Math.round((GIFT_CARD_FACTOR - 1) * 100);

export const CustomerPortalModalCashOut = ({
  isOpen,
  setIsOpen,
  value,
  onClickCheck,
  onClickPaypal,
  onClickGiftCard,
}: CustomerPortalModalCashOutProps) => (
  <Modal title='Withdraw' isOpen={isOpen} setModal={setIsOpen}>
    <Content>
      <Title>How would you like to cash out?</Title>
      <Subtitle>Redeemable</Subtitle>
      <Value>{formatValueToDollar(value, {maximumFractionDigits: 2})}</Value>
      <ButtonsContainer>
        <Button buttonStyle='primary' onClick={onClickCheck}>
          Check
        </Button>
        <PayPalButton onClick={onClickPaypal} />
        {onClickGiftCard && (
          <>
            <Button
              className={css({
                background: theme.colors.reperch_primary,
                '&:hover': {background: darken(0.1, theme.colors.reperch_primary)},
                '&:active': {
                  background: `transparent linear-gradient(180deg, ${darken(
                    0.1,
                    theme.colors.reperch_primary,
                  )} 0%, ${lighten(
                    0.1,
                    theme.colors.reperch_primary,
                  )} 100%) 0% 0% no-repeat padding-box`,
                },
                '&:disabled': {opacity: 0.5, cursor: 'not-allowed'},
              })}
              onClick={onClickGiftCard}
            >
              <img
                src={GiftCardIcon}
                alt='Gift Card'
                width={30}
                height={30}
                style={{marginRight: 8}}
              />
              <Span fontColor='white' fontWeight='bold' fontSize='small'>
                Reperch
              </Span>
              <Span fontColor='white' fontSize='small' fontWeight='normal' style={{marginLeft: 4}}>
                Gift Card
              </Span>
            </Button>
            <Span
              fontColor='reperch_primary'
              fontSize='xsmall'
              fontWeight='bold'
              style={{marginTop: -8}}
            >
              Get {extraPercentage}% extra!
            </Span>
          </>
        )}
      </ButtonsContainer>
    </Content>
  </Modal>
);
const containerBase = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});
const Content = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={containerBase} {...args} />
);
const ButtonsContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx(
      containerBase,
      css({
        marginTop: theme.spacings.large,
        gap: theme.font.sizes.xsmall,
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.large},
      }),
    )}
    {...args}
  />
);
const textBase = css({
  color: theme.colors.darkGray,
  fontFamily: theme.font.family,
  fontSize: theme.font.sizes.medium,
  fontWeight: theme.font.bold,
  textAlign: 'left',
});
const Title = (args: HTMLAttributes<HTMLParagraphElement>) => <p className={textBase} {...args} />;
const Subtitle = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cx(
      textBase,
      css({
        fontSize: theme.font.sizes.small,
        fontWeight: theme.font.light,
        textAlign: 'center',
        marginTop: theme.spacings.large,
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.medium},
      }),
    )}
    {...args}
  />
);
const Value = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cx(
      textBase,
      css({
        color: theme.colors.darkBlue,
        fontSize: theme.font.sizes.large,
        marginTop: theme.spacings.xxsmall,
      }),
    )}
    {...args}
  />
);
